import React from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormActions,
    FormAdditionalValidation,
    FormCheckbox,
    FormDynamicTextField,
    FormDynamicTextFieldInput,
    FormFields,
    FormTextField
} from "sprint-common-web";

import { useCreateLanguageForm } from "./useCreateLanguageForm";

export interface CreateLanguageFormProps {
    onCancel(): void;
    onSubmit(): void;
}

export interface CreateLanguageFormInputs {
    name: string;
    code: string;
    iso639: string;
    laRg?: string;
    isSubtitle: boolean;
    isDubbing: boolean;
    isCaption: boolean;
    buzzEmbeddedSubtitlesCodes: FormDynamicTextFieldInput[];
    buzzAudioCodes: FormDynamicTextFieldInput[];
    buzzDynamicSubtitlesCodes: FormDynamicTextFieldInput[];
    shreqAudioCodes: FormDynamicTextFieldInput[];
    shreqDynamicSubtitlesCodes: FormDynamicTextFieldInput[];
    shreqEmbeddedSubtitlesCodes: FormDynamicTextFieldInput[];
}

export const CreateLanguageForm: React.VFC<CreateLanguageFormProps> = ({
    onSubmit,
    onCancel
}: CreateLanguageFormProps) => {
    const { errors, control, formState, handleSubmit, getValues } = useForm<CreateLanguageFormInputs>({
        defaultValues: {
            name: "",
            code: "",
            iso639: "",
            laRg: "",
            isSubtitle: false,
            isDubbing: false,
            isCaption: false,
            buzzEmbeddedSubtitlesCodes: [],
            buzzAudioCodes: [],
            buzzDynamicSubtitlesCodes: [],
            shreqAudioCodes: [],
            shreqDynamicSubtitlesCodes: [],
            shreqEmbeddedSubtitlesCodes: []
        },
        criteriaMode: "all"
    });

    const {
        validateLanguageType,
        addLanguage,
        validateCode,
        validateSubtitleBuzzCode,
        validateAudioBuzzCode,
        validateAudioShreqCode,
        validateSubtitleShreqCode
    } = useCreateLanguageForm();

    const handleCancelButtonClick = () => {
        onCancel();
    };

    const handleCreateLanguage = async (inputs: CreateLanguageFormInputs) => {
        await addLanguage(inputs);
        onSubmit();
    };
    return (
        <Form onSubmit={handleSubmit(handleCreateLanguage)}>
            <FormFields>
                <FormTextField
                    label="Name"
                    control={control}
                    name="name"
                    errors={errors}
                    rules={{ required: "Enter name" }}
                />
                <FormTextField
                    label="Code"
                    control={control}
                    name="code"
                    errors={errors}
                    rules={{ required: "Enter code", validate: validateCode }}
                />
                <FormTextField
                    label="ISO 639"
                    control={control}
                    name="iso639"
                    errors={errors}
                    rules={{ required: "Enter iso 639" }}
                />
                <FormTextField label="LaRg" control={control} name="laRg" errors={errors} />
                <FormCheckbox label="Is subtitle" name="isSubtitle" control={control} />
                <FormCheckbox label="Is dubbing" name="isDubbing" control={control} />
                <FormCheckbox label="Is caption" name="isCaption" control={control} />
                <FormAdditionalValidation
                    control={control}
                    name="additionalValidation"
                    errors={errors}
                    rules={{ validate: () => validateLanguageType(getValues()) }}
                />
                <FormDynamicTextField
                    rules={{ validate: (buzzCode) => validateAudioBuzzCode(buzzCode, getValues()) }}
                    control={control}
                    errors={errors}
                    label="Buzz audio code"
                    name="buzzAudioCodes"
                />
                <FormDynamicTextField
                    rules={{ validate: (buzzCode) => validateSubtitleBuzzCode(buzzCode, getValues()) }}
                    control={control}
                    errors={errors}
                    label="Buzz embedded subtitles code"
                    name="buzzEmbeddedSubtitlesCodes"
                />
                <FormDynamicTextField
                    rules={{ validate: (buzzCode) => validateSubtitleBuzzCode(buzzCode, getValues()) }}
                    control={control}
                    errors={errors}
                    label="Buzz dynamic subtitles code"
                    name="buzzDynamicSubtitlesCodes"
                />
                <FormDynamicTextField
                    rules={{ validate: (buzzCode) => validateAudioShreqCode(buzzCode, getValues()) }}
                    control={control}
                    errors={errors}
                    label="Shreq audio code"
                    name="shreqAudioCodes"
                />
                <FormDynamicTextField
                    rules={{ validate: (buzzCode) => validateSubtitleShreqCode(buzzCode, getValues()) }}
                    control={control}
                    errors={errors}
                    label="Shreq embedded subtitles code"
                    name="shreqEmbeddedSubtitlesCodes"
                />
                <FormDynamicTextField
                    rules={{ validate: (buzzCode) => validateSubtitleShreqCode(buzzCode, getValues()) }}
                    control={control}
                    errors={errors}
                    label="Shreq dynamic subtitles code"
                    name="shreqDynamicSubtitlesCodes"
                />
            </FormFields>
            <FormActions
                isLoading={formState.isSubmitting || formState.isValidating}
                onCancel={handleCancelButtonClick}
            />
        </Form>
    );
};
